import type { ReportContent, Sample } from './report';
import type { TemplateDoc } from './template';

/** The choices every lab starts with. Labs add their own by typing them in. */
export const STANDARD_SAMPLE_TYPES = ['Blood', 'Urine', 'Semen'] as const;

/** Turns what a template says ("Whole Blood EDTA", "Serum", "Urine, early morning") into a sample type. */
export function sampleKindOf(text: string | undefined): string | null {
  const t = (text ?? '').trim();
  if (!t) return null;
  const l = t.toLowerCase();
  if (/(blood|serum|plasma|edta|citrate|fluoride|heparin)/.test(l)) return 'Blood';
  if (/(urine|urinary)/.test(l)) return 'Urine';
  if (/(semen|seminal)/.test(l)) return 'Semen';
  return t;
}

const same = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** One sample for each kind of sample the chosen tests need, collected now. */
export function defaultSamples(
  docs: readonly Pick<TemplateDoc, 'sampleType'>[],
  now: Date,
  makeId: () => string,
): Sample[] {
  const kinds: string[] = [];
  for (const d of docs) {
    const k = sampleKindOf(d.sampleType);
    if (k && !kinds.some((x) => same(x, k))) kinds.push(k);
  }
  return kinds.map((type) => ({ id: makeId(), type, collectedAt: now.toISOString() }));
}

/** "Blood · ID 4471" for a short line. */
export function describeSample(s: Sample): string {
  return s.identifier?.trim() ? `${s.type} · ID ${s.identifier.trim()}` : s.type;
}

/** The earliest time a sample was collected, as ISO text. */
export function earliestCollected(samples: readonly Sample[]): string | null {
  const times = samples
    .map((s) => (s.collectedAt ? Date.parse(s.collectedAt) : Number.NaN))
    .filter((n) => !Number.isNaN(n));
  return times.length ? new Date(Math.min(...times)).toISOString() : null;
}

/** When the samples of a report were collected: from the samples, or the older single date. */
export function collectedAtOf(content: ReportContent): string | null {
  return earliestCollected(content.samples) ?? content.visit.collectedAt ?? null;
}

/** The standard types plus the lab's own, without repeats, in a stable order. */
export function sampleTypeOptions(own: readonly string[]): string[] {
  const out: string[] = [...STANDARD_SAMPLE_TYPES];
  for (const name of [...own].sort((a, b) => a.localeCompare(b))) {
    if (name.trim() && !out.some((x) => same(x, name))) out.push(name.trim());
  }
  return out;
}

/** "Blood ×2 (ID 4471, 4472), Urine" for the line on a report. */
export function sampleSummary(samples: readonly Sample[]): string {
  const order: string[] = [];
  const by = new Map<string, Sample[]>();
  for (const s of samples) {
    const key = s.type.trim().toLowerCase();
    if (!by.has(key)) {
      by.set(key, []);
      order.push(key);
    }
    by.get(key)?.push(s);
  }
  return order
    .map((key) => {
      const group = by.get(key) as Sample[];
      const name = (group[0] as Sample).type.trim();
      const ids = group.map((g) => g.identifier?.trim()).filter(Boolean);
      const count = group.length > 1 ? ` ×${group.length}` : '';
      return ids.length ? `${name}${count} (ID ${ids.join(', ')})` : `${name}${count}`;
    })
    .join(', ');
}
